import { onSettings, settings } from './settings';

/**
 * Muzyka generowana na żywo (WebAudio, bez plików): ciepły podkład smyczkowy z akordów
 * i spokojna melodia na harfie w skali doryckiej. Cztery nastroje:
 *  obóz — wolno i jasno, wyprawa — ciemniej z pulsującym basem, boss — szybciej, z bębnem,
 *  szał bossa — jeszcze szybciej, półtonowe napięcie i gęsty bęben.
 */
export type MusicMood = 'hub' | 'run' | 'boss' | 'enrage';

const ROOT = 146.83; // D3
const DORIAN = [0, 2, 3, 5, 7, 9, 10];
const PROGRESSIONS: Record<MusicMood, number[][]> = {
  hub: [[0, 4, 7], [5, 9, 12], [3, 7, 10], [7, 10, 14]],
  run: [[0, 3, 7], [-2, 2, 5], [-4, 0, 3], [-2, 2, 5]],
  boss: [[0, 3, 7], [1, 5, 8], [0, 3, 7], [-2, 1, 5]],
  enrage: [[0, 3, 6], [1, 4, 7], [0, 3, 6], [-1, 2, 5]],
};
const TEMPO: Record<MusicMood, number> = { hub: 3.6, run: 3.0, boss: 1.8, enrage: 1.4 };

const freq = (semi: number) => ROOT * 2 ** (semi / 12);

class Music {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private reverb: ConvolverNode | null = null;
  private mood: MusicMood = 'hub';
  private bar = 0;
  private nextBar = 0;

  constructor() {
    const unlock = () => this.start();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    onSettings((s) => {
      if (this.master && this.ctx) this.master.gain.setTargetAtTime(s.music * 0.22, this.ctx.currentTime, 0.3);
    });
  }

  setMood(mood: MusicMood): void {
    if (mood === this.mood) return;
    this.mood = mood;
    // Zmiana nastroju od następnego taktu, bez urwania dźwięku.
    if (this.ctx) this.nextBar = Math.min(this.nextBar, this.ctx.currentTime + 0.4);
  }

  private start(): void {
    if (this.ctx) return;
    try {
      this.ctx = new AudioContext();
    } catch {
      return;
    }
    const ctx = this.ctx;
    this.master = ctx.createGain();
    this.master.gain.value = settings.music * 0.22;
    // Pogłos z syntetycznej odpowiedzi impulsowej (szum zanikający) — przestrzeń jak w kamiennej sali.
    this.reverb = ctx.createConvolver();
    const len = ctx.sampleRate * 2.8;
    const ir = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = ir.getChannelData(ch);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 3;
    }
    this.reverb.buffer = ir;
    const wet = ctx.createGain();
    wet.gain.value = 0.55;
    this.reverb.connect(wet).connect(this.master);
    this.master.connect(ctx.destination);
    this.nextBar = ctx.currentTime + 0.2;
    window.setInterval(() => this.schedule(), 200);
  }

  /** Planuje takty z wyprzedzeniem (stabilny rytm mimo nierównych klatek). */
  private schedule(): void {
    const ctx = this.ctx;
    if (!ctx || settings.music <= 0) return;
    while (this.nextBar < ctx.currentTime + 1.2) {
      this.playBar(this.nextBar);
      this.nextBar += TEMPO[this.mood];
      this.bar++;
    }
  }

  private playBar(t: number): void {
    const mood = this.mood;
    const len = TEMPO[mood];
    const chord = PROGRESSIONS[mood][this.bar % 4]!;
    // Podkład: akord na rozstrojonych piłach przez filtr — miękka „smyczkowa” plama.
    for (const n of chord) this.pad(freq(n - 12), t, len * 1.05, mood === 'hub' ? 900 : 650);
    // Bas: na wyprawie pulsuje, u bossa gra ósemki.
    const bassHits = mood === 'hub' ? 1 : mood === 'run' ? 2 : mood === 'boss' ? 4 : 8;
    for (let i = 0; i < bassHits; i++) this.pluck(freq(chord[0]! - 24), t + (i * len) / bassHits, 0.9, 'triangle', 0.16);
    // Melodia: kilka nut harfy ze skali, losowo, ale zawsze w tonacji.
    const notes = mood === 'boss' || mood === 'enrage' ? 6 : 4;
    for (let i = 0; i < notes; i++) {
      if (Math.random() < 0.35) continue;
      const degree = DORIAN[Math.floor(Math.random() * DORIAN.length)]!;
      const octave = Math.random() < 0.3 ? 24 : 12;
      this.pluck(freq(degree + octave), t + (i * len) / notes + Math.random() * 0.05, 1.6, 'sine', 0.07);
    }
    if (mood === 'boss') for (let i = 0; i < 4; i++) this.drum(t + (i * len) / 4, i % 2 === 0 ? 0.35 : 0.2);
    if (mood === 'enrage') for (let i = 0; i < 8; i++) this.drum(t + (i * len) / 8, i % 4 === 0 ? 0.45 : i % 2 === 0 ? 0.28 : 0.16);
  }

  private pad(f: number, t: number, dur: number, cutoff: number): void {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(0.05, t + dur * 0.35);
    g.gain.linearRampToValueAtTime(0.0001, t + dur);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = cutoff;
    filter.connect(g);
    g.connect(this.master!);
    g.connect(this.reverb!);
    for (const detune of [-8, 7]) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      o.detune.value = detune;
      o.connect(filter);
      o.start(t);
      o.stop(t + dur + 0.1);
    }
  }

  private pluck(f: number, t: number, dur: number, type: OscillatorType, gain: number): void {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = f;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g);
    g.connect(this.master!);
    g.connect(this.reverb!);
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  /** Bęben: krótki spadający ton (jak kocioł). */
  private drum(t: number, gain: number): void {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.frequency.setValueAtTime(120, t);
    o.frequency.exponentialRampToValueAtTime(45, t + 0.25);
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
    o.connect(g).connect(this.master!);
    o.start(t);
    o.stop(t + 0.4);
  }
}

export const music = new Music();
